import Amount from "@funds/components/atoms/amount";
import {
  calculateExpectedCorpus,
  calculateFutureValue,
  calculateMonthlySIP,
  calculateTotalSipInvested,
  convertToCurrency,
  getProposalStepUp,
} from "creso-core/utils";
import { StepupFrequency } from "creso-core/constants";
import { ComponentProps, useMemo } from "react";
import {
  ChartTooltipContent,
  Checkbox,
  cn,
  CurrencyInput,
  GappedChart,
  NumberCounterInput,
  Switch,
  V2Label,
} from "web-design-system";
import { useWatch } from "react-hook-form";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import { InfoIcon } from "lucide-react";
import {
  StepUpSipConfigComponent,
  StepUpSipValue,
} from "@transactions/components/sip-step-up-config";
import { useProposalFormContext } from "../../providers/proposal-provider";
import { durationDisplayFormat } from "../../config";

const STEP_UP_FREQUENCY_OPTIONS = [
  { label: "12 months", value: StepupFrequency.YEARLY },
  { label: "6 months", value: StepupFrequency.HALFYEARLY },
];

export function ProposalSetup() {
  const form = useProposalFormContext();
  const goal = form.watch("goal");

  const [
    lumpsumAmount,
    sipAmount,
    expectedReturn,
    includeLinkedHoldings,
    durationMonths = 12,
    stepUpAmount,
    stepUpPercentage,
    stepUpFrequency,
    proposalFunds,
  ] = useWatch({
    control: form.control,
    name: [
      "total_lumpsum_amount",
      "total_sip_amount",
      "expected_return",
      "include_linked_holdings",
      "duration_months",
      "step_up_amount",
      "step_up_percentage",
      "step_up_frequency",
      "proposal_funds",
    ],
  });

  const sipFundCount = proposalFunds.filter((f) => f.type === "SIP").length;

  const computeStepUp = (value: StepUpSipValue) =>
    getProposalStepUp(
      {
        step_up_amount: value.amount,
        step_up_percentage: value.percentage,
        step_up_frequency: value.frequency,
      },
      sipFundCount
    );

  const stepUp = useMemo(
    () =>
      computeStepUp({
        amount: stepUpAmount,
        percentage: stepUpPercentage,
        frequency: stepUpFrequency,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stepUpAmount, stepUpPercentage, stepUpFrequency, sipFundCount]
  );

  const annualReturn = Number(expectedReturn || 12);

  // Future value of the goal's target corpus - everything else (linked
  // holdings + lumpsum + SIP, each grown at the expected return) needs to
  // add up to this "goal amount".
  const goalAmount = useMemo(() => {
    if (!goal?.id) return 0;
    return Math.round(
      calculateFutureValue(
        Number(goal.target_corpus),
        Number(goal.expected_inflation),
        goal.duration_months
      )
    );
  }, [goal]);

  // Linked holdings are assumed to keep growing at the configured expected
  // return until the goal duration ends, so they're grown to their future
  // value before being netted off the goal amount. Takes the return rate,
  // duration, and include-flag as arguments (rather than closing over
  // `annualReturn`/`durationMonths`/`includeLinkedHoldings`) so callers can
  // pass values that haven't been committed to the form yet.
  const computeLinkedHoldingsFutureValue = (
    returnRate: number,
    months: number,
    include: boolean = includeLinkedHoldings
  ) => {
    if (!goal?.id || !include) return 0;
    return Math.round(
      calculateFutureValue(
        Number(goal?.total_current_value ?? 0),
        returnRate,
        months
      )
    );
  };

  const linkedHoldingsFutureValue = useMemo(
    () => computeLinkedHoldingsFutureValue(annualReturn, durationMonths),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [goal, includeLinkedHoldings, annualReturn, durationMonths]
  );

  // Target that lumpsum + SIP (with their own growth) need to reach so
  // that, combined with linked holdings, the total equals the goal amount.
  const computeProposalAmount = (
    returnRate: number,
    months: number,
    include: boolean = includeLinkedHoldings
  ) => {
    if (!goal?.id) return 0;
    const linkedFV = computeLinkedHoldingsFutureValue(
      returnRate,
      months,
      include
    );
    return Math.max(goalAmount - linkedFV, 0);
  };

  const proposalAmount = useMemo(
    () => computeProposalAmount(annualReturn, durationMonths),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [goal, includeLinkedHoldings, annualReturn, durationMonths]
  );

  // Amount lumpsum + SIP actually grow to, given what's currently entered
  const expectedCorpus = useMemo(() => {
    return Math.round(
      calculateExpectedCorpus(
        Number(sipAmount || 0),
        Number(lumpsumAmount || 0),
        annualReturn,
        durationMonths,
        stepUp
      )
    );
  }, [lumpsumAmount, sipAmount, annualReturn, durationMonths, stepUp]);

  // What linked holdings + the currently entered lumpsum + SIP actually
  // grow to. The auto-adjust handlers try to keep this equal to
  // `goalAmount`, but each of lumpsum/SIP/include-linked-holdings can be
  // changed independently (and SIP/lumpsum can be typed manually to any
  // value), and neither can go negative to compensate for the other being
  // too large - so this can end up above the goal amount from any of them:
  // linked holdings alone, lumpsum alone, SIP alone, or any combination.
  const actualTotalCorpus = expectedCorpus + linkedHoldingsFutureValue;

  // Small rounding drift (SIP is rounded to the nearest rupee before being
  // stored) is expected even when on target, so only flag a meaningful
  // overshoot.
  const exceedsGoalAmount =
    !!goal?.id &&
    goalAmount > 0 &&
    (actualTotalCorpus - goalAmount) / goalAmount > 0.01;

  // Corpus amount shown below the investment split graph. When on target,
  // show the goal amount itself rather than the rounding-drift-prone
  // recomputation from lumpsum + SIP; when it's actually been exceeded,
  // show the real (larger) corpus so the number matches the warning below.
  const totalCorpus = !goal?.id
    ? expectedCorpus
    : exceedsGoalAmount
      ? actualTotalCorpus
      : goalAmount;

  // Handler for lumpsum changes - adjusts SIP
  const handleLumpsumChange = (val: number) => {
    form.setValue("total_lumpsum_amount", val.toString());

    // Only auto-adjust if goal is linked
    if (!goal?.id) return;

    // Calculate required SIP to reach proposal amount
    const requiredSIP = Math.round(
      calculateMonthlySIP(
        proposalAmount,
        annualReturn,
        durationMonths,
        val,
        stepUp
      )
    );

    form.setValue("total_sip_amount", requiredSIP.toString(), {
      shouldValidate: false,
    });
  };

  // Handler for SIP changes - adjusts lumpsum
  const handleSipChange = (val: number) => {
    form.setValue("total_sip_amount", val.toString());

    // Only auto-adjust if goal is linked
    if (!goal?.id) return;

    // Calculate future value of SIP using the shared utility
    const sipFV = calculateExpectedCorpus(
      val,
      0,
      annualReturn,
      durationMonths,
      stepUp
    );

    // Remaining future value that lumpsum must cover
    const remainingFV = proposalAmount - sipFV;

    if (remainingFV <= 0) {
      form.setValue("total_lumpsum_amount", "0", { shouldValidate: false });
      return;
    }

    // Calculate required lumpsum (present value of remaining amount)
    let requiredLumpsum: number;
    if (annualReturn > 0) {
      const monthlyRate = (1 + annualReturn / 100) ** (1 / 12) - 1;
      requiredLumpsum = remainingFV / (1 + monthlyRate) ** durationMonths;
    } else {
      requiredLumpsum = remainingFV;
    }

    requiredLumpsum = Math.round(requiredLumpsum);

    form.setValue("total_lumpsum_amount", requiredLumpsum.toString(), {
      shouldValidate: false,
    });
  };

  // Handler for expected return changes - adjusts SIP
  const handleExpectedReturnChange = (val: number) => {
    form.setValue("expected_return", val.toString());

    // Only auto-adjust if goal is linked
    if (!goal?.id) return;

    // Calculate required SIP to reach proposal amount, using the new
    // return rate since `proposalAmount` won't reflect it until re-render
    const requiredSIP = Math.round(
      calculateMonthlySIP(
        computeProposalAmount(val, durationMonths),
        val,
        durationMonths,
        Number(lumpsumAmount || 0),
        stepUp
      )
    );

    form.setValue("total_sip_amount", requiredSIP.toString(), {
      shouldValidate: false,
    });
  };

  // Handler for include-linked-holdings toggle - adjusts SIP since
  // toggling changes how much of the goal amount lumpsum + SIP need to cover
  const handleIncludeLinkedHoldingsChange = (checked: boolean) => {
    form.setValue("include_linked_holdings", checked);

    if (!goal?.id) return;

    const requiredSIP = Math.round(
      calculateMonthlySIP(
        computeProposalAmount(annualReturn, durationMonths, checked),
        annualReturn,
        durationMonths,
        Number(lumpsumAmount || 0),
        stepUp
      )
    );

    form.setValue("total_sip_amount", requiredSIP.toString(), {
      shouldValidate: false,
    });
  };

  // Handler for step-up changes - adjusts SIP since a step-up means a
  // smaller starting installment reaches the same goal amount
  const handleStepUpChange = (value: StepUpSipValue) => {
    form.setValue("step_up_amount", value.amount, {
      shouldValidate: form.formState.isSubmitted,
    });
    form.setValue("step_up_percentage", value.percentage, {
      shouldValidate: form.formState.isSubmitted,
    });
    form.setValue("step_up_frequency", value.frequency);

    if (!goal?.id) return;

    const requiredSIP = Math.round(
      calculateMonthlySIP(
        proposalAmount,
        annualReturn,
        durationMonths,
        Number(lumpsumAmount || 0),
        computeStepUp(value)
      )
    );

    form.setValue("total_sip_amount", requiredSIP.toString(), {
      shouldValidate: false,
    });
  };

  const linkedHoldingsValue = includeLinkedHoldings
    ? Number(goal?.total_current_value ?? 0)
    : 0;

  // Growth on linked holdings isn't part of
  // expectedCorpus (that only covers lumpsum + SIP),
  // so it's added to the returns segment separately.
  const linkedHoldingsGrowth = Math.max(
    linkedHoldingsFutureValue - linkedHoldingsValue,
    0
  );

  const totalSipInvested = calculateTotalSipInvested(
    Number(sipAmount || 0),
    durationMonths,
    stepUp
  );

  const investedTotal = Number(lumpsumAmount || 0) + totalSipInvested;
  const expectedReturnsValue =
    Math.max(expectedCorpus - investedTotal, 0) + linkedHoldingsGrowth;

  return (
    <div className="flex flex-col flex-1">
      <div className="flex mb-8 flex-col space-y-4">
        {goal?.id && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Checkbox
                id="include-linked-holdings"
                checked={includeLinkedHoldings}
                onCheckedChange={(checked) =>
                  handleIncludeLinkedHoldingsChange(!!checked)
                }
              />
              <V2Label htmlFor="include-linked-holdings" className="text-sm">
                Include linked holdings
              </V2Label>
            </div>
            <div className="w-48">
              <CurrencyInput
                disabled
                value={Math.round(goal?.total_current_value ?? 0)}
                decimalsLimit={0}
              />
            </div>
          </div>
        )}

        <div className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="size-3.5 rounded bg-chart-2" />
            <V2Label className="font-normal text-sm">
              One time investment
            </V2Label>
          </div>
          <div className="w-48">
            <CurrencyInput
              placeholder="₹ Amount"
              value={lumpsumAmount}
              decimalsLimit={0}
              onValueChange={(val) => handleLumpsumChange(Number(val || 0))}
            />
          </div>
        </div>

        <div className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="size-3.5 rounded bg-chart-3" />
            <V2Label className="font-normal text-sm">Monthly SIP</V2Label>
          </div>
          <div className="w-48">
            <CurrencyInput
              placeholder="₹ Amount"
              value={sipAmount}
              decimalsLimit={0}
              onValueChange={(val) => handleSipChange(Number(val || 0))}
            />
          </div>
        </div>
      </div>

      <StepUpSipConfigComponent
        variant="card"
        className="mb-6"
        description="(Applies to each fund)"
        frequencyOptions={STEP_UP_FREQUENCY_OPTIONS}
        defaultFrequency={StepupFrequency.YEARLY}
        value={{
          amount: stepUpAmount,
          percentage: stepUpPercentage,
          frequency: stepUpFrequency,
        }}
        onChange={handleStepUpChange}
        errors={{
          amount: form.formState.errors.step_up_amount?.message,
          percentage: form.formState.errors.step_up_percentage?.message,
        }}
      />

      <div className="flex flex-col gap-4 p-4 bg-card rounded-lg border border-border">
        {/* Calculate returns toggle */}
        <div className="flex items-center justify-between gap-2">
          <V2Label
            className="text-sm font-semibold"
            htmlFor="calculate-returns"
          >
            Calculate returns
          </V2Label>
          {!goal?.id ? (
            <Switch
              id="calculate-returns"
              checked={!!form.watch("projected_value")}
              onCheckedChange={(checked) =>
                form.setValue(
                  "projected_value",
                  checked ? expectedCorpus.toString() : null
                )
              }
            />
          ) : null}
        </div>

        {/* Return calculation inputs - only shown when checkbox is checked */}
        {(goal?.id || !!form.watch("projected_value")) && (
          <div className="flex flex-col space-y-4">
            <div className="flex flex-row items-center justify-between">
              <V2Label className="font-normal text-sm">
                Expected rate of return
              </V2Label>
              <div className="w-48">
                <NumberCounterInput
                  value={expectedReturn || "12"}
                  onChange={handleExpectedReturnChange}
                  step={0.5}
                  displayFormatter={(v) => `${v}%`}
                  precision={1}
                  min={0}
                  max={100}
                />
              </div>
            </div>

            <div
              className={cn(
                "flex flex-row items-center justify-between",
                !!goal?.id && "opacity-50"
              )}
            >
              <V2Label className="text-sm font-normal">
                Duration of investment
              </V2Label>
              <div className="w-48">
                <NumberCounterInput
                  value={durationMonths / 12}
                  onChange={(val) => {
                    form.setValue("duration_months", Math.round(val * 12));
                  }}
                  disabled={!!goal?.id}
                  step={1}
                  displayFormatter={durationDisplayFormat}
                  precision={2}
                  min={0.1}
                  max={100}
                />
              </div>
            </div>
          </div>
        )}

        {/* Investment split graph + expected corpus */}
        <ConditionalRenderer
          condition={!!goal?.id || !!form.watch("projected_value")}
        >
          <InvestmentSplitBar
            linkedHoldings={linkedHoldingsValue}
            oneTime={Number(lumpsumAmount || 0)}
            totalSip={totalSipInvested}
            expectedReturns={expectedReturnsValue}
          />
          <div className="flex items-center justify-end gap-2">
            <span className="text-sm text-muted-foreground">
              Corpus amount:
            </span>
            <span className="text-sm font-semibold">
              <Amount precision={0}>{totalCorpus}</Amount>
            </span>
          </div>
        </ConditionalRenderer>
      </div>

      <ConditionalRenderer condition={exceedsGoalAmount}>
        <div className="py-2 px-3 items-center gap-2 bg-card rounded-lg border border-border flex mt-auto">
          <InfoIcon className="size-4" />
          <span className="text-right text-sm text-muted-foreground">
            You are exceeding the goal amount
          </span>
        </div>
      </ConditionalRenderer>
    </div>
  );
}

function InvestmentSplitBar({
  linkedHoldings,
  oneTime,
  totalSip,
  expectedReturns,
}: {
  linkedHoldings: number;
  oneTime: number;
  totalSip: number;
  expectedReturns: number;
}) {
  const segments = [
    linkedHoldings > 0 && {
      key: "LinkedHoldings",
      value: linkedHoldings,
      color: "var(--primary)",
      label: "Linked holdings",
    },
    {
      key: "OneTime",
      value: oneTime,
      color: "var(--chart-2)",
      label: "One-time",
    },
    {
      key: "TotalSip",
      value: totalSip,
      color: "var(--chart-3)",
      label: "Total SIP",
    },
    {
      key: "ExpectedReturns",
      value: expectedReturns,
      color: "var(--chart-4)",
      label: "Total Expected returns",
    },
  ].filter(Boolean) as ComponentProps<typeof GappedChart>["segments"];

  return (
    <GappedChart
      className="relative h-2 w-full"
      segments={segments}
      tooltipContent={
        <ChartTooltipContent
          hideLabel
          className="px-3 pt-3 pb-2"
          formatter={(value, name, item) => (
            <div className="flex flex-1 pb-1 items-center gap-8 justify-between leading-none">
              <div className="flex items-center gap-2">
                <div
                  className="size-2.5 shrink-0 rounded-[2px]"
                  style={{ backgroundColor: item.payload?.fill }}
                />
                <span className="text-xs">{name}</span>
              </div>
              <span className="text-foreground tabular-nums">
                {convertToCurrency(Number(value), 0)}
              </span>
            </div>
          )}
        />
      }
    />
  );
}
